import { Head, useForm } from '@inertiajs/react'
import { Card } from '../../components/core/Card.jsx'
import { Button } from '../../components/core/Button.jsx'
import { Field } from '../../components/forms/Field.jsx'
import { Input } from '../../components/forms/Input.jsx'
import { Alert } from '../../components/feedback/Alert.jsx'
import { withSignedInLayout } from '../../signed_in/SignedInLayout.jsx'
import { IndustryMatchCard, stillAsking } from '../../shared/IndustryMatch.jsx'

// GET /brands/:id/review — generated questions, read before anything is
// collected for them. A business outside the curated seven reaches this straight
// from setup, and its dashboard sends it back here until it is confirmed.
//
// **Read-only, by the founder's decision on 2026-10-12.** Hand-edited questions
// drift towards things nobody searches, so a set is confirmed whole or replaced
// whole: the customer's lever is the description, never a question. There is no
// control on any row, deliberately — the same rule the Questions page follows.
//
// Four forms, one per action, each its own `useForm` so the contract test reads
// each submit separately. The fourth, using a listed industry, appears only when
// a new description reads as one.

export default function BrandReview({
  brand = null,
  prompts = [],
  reported = false,
  industry_match = null,
  owner_email = '',
  stop = 'none',
  stop_sentence = null,
  errors = {},
}) {
  const confirm = useForm({})
  const regenerate = useForm({ industry: (brand && brand.industry) ?? '', industry_unlisted: false })
  // `vertical` is set from the match at the moment of sending: the match
  // arrives after this form was created, and the state is kept across that
  // redirect rather than rebuilt.
  const listed = useForm({ vertical: '' })
  const report = useForm({})
  const asking = !regenerate.data.industry_unlisted && stillAsking(industry_match, regenerate.data.industry)

  const commercial = prompts.filter((p) => p.commercial_intent)
  const other = prompts.filter((p) => !p.commercial_intent)
  const where = brand ? [brand.city, brand.state].filter(Boolean).join(', ') : ''

  return (
    <>
      <Head title="Check the questions" />
      <main className="mx-auto min-h-screen w-full max-w-3xl px-6 py-10">
        <header className="mb-8 flex flex-col gap-2">
          <h1 className="page-title">Check the questions</h1>
          <p className="max-w-[66ch] text-[15px] text-[color:var(--text-body)]">
            These are the {prompts.length} questions AI assistants will be asked each week about{' '}
            {brand?.industry} in {where}, and the answers are what {brand?.name}'s figures are counted
            from. Read them as a customer would: they should sound like what people ask when they are
            looking for a business like yours.
          </p>
        </header>

        <div className="flex flex-col gap-6">
          <Card title="Questions that ask who to use" description="These build your visibility figure">
            <QuestionList rows={commercial} start={1} />
          </Card>

          <Card title="Questions that ask how to choose, or what things cost" description="These build the source map">
            <QuestionList rows={other} start={commercial.length + 1} />
          </Card>

          <Card title="Start with these" description="Nothing is collected until you do">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                confirm.post(`/brands/${brand.id}/review/confirm`)
              }}
              className="flex flex-wrap items-center gap-4"
            >
              <Button variant="primary" size="lg" as="button" loading={confirm.processing}>
                Start measuring with these
              </Button>
              <span className="max-w-[46ch] text-[13.5px] text-[color:var(--text-muted)]">
                {stop === null ? 'The first collection starts as soon as you do.' : stop_sentence}
              </span>
            </form>
          </Card>

          <Card title="Not the right kind of business?" description="Change the description for a new set">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                regenerate.post(`/brands/${brand.id}/review/regenerate`)
              }}
              className="flex flex-col gap-4"
            >
              <Field
                label="What kind of business is it?"
                htmlFor="industry"
                hint="A new set of questions is written from these words, in place of the one above."
                error={errors.industry}
              >
                <Input
                  id="industry"
                  name="industry"
                  maxLength={60}
                  value={regenerate.data.industry}
                  invalid={Boolean(errors.industry)}
                  onChange={(e) => {
                    const industry = e.target.value
                    regenerate.setData((d) => ({ ...d, industry, industry_unlisted: false }))
                  }}
                />
              </Field>
              <div>
                <Button variant="secondary" as="button" loading={regenerate.processing} disabled={asking}>
                  Write a new set
                </Button>
              </div>
            </form>
          </Card>

          {/*
            Here "use it" sends at once, unlike setup: the business exists, and
            a listed industry's questions need no reading first — so the
            sentence beside the confirm button, which says whether collecting
            starts, is repeated under it.
          */}
          {asking && (
            <IndustryMatchCard
              match={industry_match}
              useLabel={`Start measuring with ${industry_match.label}`}
              onUse={() => {
                listed.setData('vertical', industry_match.key)
                listed.post(`/brands/${brand.id}/review/listed`)
              }}
              onKeep={() => {
                regenerate.setData('industry_unlisted', true)
                regenerate.post(`/brands/${brand.id}/review/regenerate`)
              }}
              processing={listed.processing || regenerate.processing}
              note={stop === null ? 'The first collection starts as soon as you do.' : stop_sentence}
            />
          )}

          {/*
            The escape hatch in the decision. It sends the operator the set and
            the customer's address; it promises nothing beyond that, because
            what happens next is a person's judgement rather than a feature.
          */}
          {reported ? (
            <Alert tone="data" icon="mail" title="Sent to us, with these questions">
              You can still describe the business differently, or start with these. Any reply comes to{' '}
              {owner_email}.
            </Alert>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                report.post(`/brands/${brand.id}/review/report`)
              }}
            >
              <Button variant="ghost" size="sm" as="button" loading={report.processing}>
                These don't fit my business
              </Button>
            </form>
          )}
        </div>
      </main>
    </>
  )
}

// Numbered across both cards, as one list of twenty, so "question 12" means the
// same thing wherever it is said.
function QuestionList({ rows, start }) {
  return (
    <ol className="flex flex-col" start={start}>
      {rows.map((row, i) => (
        <li
          key={`${row.intent}-${i}`}
          className="flex gap-3 border-t border-[color:var(--border-subtle)] py-2.5 first:border-t-0 first:pt-0"
        >
          <span className="w-6 shrink-0 font-mono text-[12.5px] tabular-nums text-[color:var(--text-muted)]">
            {start + i}
          </span>
          <span className="text-[14px] text-[color:var(--text-body)]">{row.text}</span>
        </li>
      ))}
    </ol>
  )
}

BrandReview.layout = withSignedInLayout
